import React from 'react';
import { ArrowRight } from 'lucide-react';
import { useSplitReveal } from '../lib/useSplitReveal';
import { useParallaxMedia } from '../lib/useParallaxMedia';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1747640731141-7d81ade721bd?q=80&w=900&auto=format&fit=crop';

const scrollToSignup = () =>
  document.getElementById('signup-section')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

const HeroSection: React.FC = () => {
  // The headline reveals line by line; the supporting copy and the portrait
  // follow on the CSS `.enter` timings, which are already tuned to it.
  const headline = useSplitReveal<HTMLHeadingElement>({ delay: 0.15 });
  const portrait = useParallaxMedia<HTMLDivElement>({ drift: 14, delay: 0.1, immediate: true });

  return (
    <section className="relative min-h-[100svh] pt-16 sm:pt-20 lg:pt-24">
      <div className="shell grid min-h-[calc(100svh-5rem)] grid-cols-1 items-center gap-7 pb-8 sm:min-h-[calc(100svh-6rem)] sm:gap-10 sm:pb-16 lg:grid-cols-12 lg:gap-14 lg:pb-24">
        {/* Type block: left-weighted, deliberately wider than the image column. */}
        <div className="lg:col-span-7">
          <h1
            ref={headline}
            className="display split-target max-w-[13ch] text-[clamp(2.5rem,10vw,5.5rem)] font-medium text-ink"
          >
            Only what actually suits you.
          </h1>

          <p className="enter enter-2 mt-5 max-w-[46ch] text-[15px] leading-relaxed text-muted sm:mt-7 sm:text-[17px] md:text-lg">
            Upload one photo. DressUp reads your undertone and contrast, then builds a marketplace
            stocked only with your colors.
          </p>

          <div className="enter enter-3 mt-6 flex flex-col gap-3 sm:mt-10 sm:flex-row sm:items-center">
            <button type="button" onClick={scrollToSignup} className="btn btn-primary group">
              Get early access
              <ArrowRight
                strokeWidth={1.5}
                className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1"
              />
            </button>
            <a
              href="https://app.thedressup.co/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-ghost"
            >
              Open the web app
            </a>
          </div>
        </div>

        {/* Portrait asset, offset downward so the composition sits off-axis. */}
        {/* Photo: Unsplash (free to use, no attribution required). */}
        <div className="relative lg:col-span-5 lg:mt-16">
          <div ref={portrait} className="overflow-hidden bg-surface">
            <img
              src={HERO_IMAGE}
              alt="A person in a bright patterned knit and cap against a deep red backdrop"
              width={900}
              height={1125}
              loading="eager"
              decoding="async"
              className="h-[38svh] w-full object-cover object-center sm:h-[52svh] lg:h-[62svh]"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
